import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  ModalProps,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import type { BankAccount } from '../../../../../types';
import { BankAccountState, BankAccountType } from '../../../../../enums';

export type StateReasonModalType = 'show' | 'edit';
type StateReasonModalProps = {
  open: boolean;
  onSave: () => void;
  onCancel: () => void;
  fields: Partial<BankAccount> | null;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
};

export const EditBankAccount: FC<StateReasonModalProps> = (props) => {
  const { open, onSave, onCancel, fields, fieldsHandler } = props;
  const { t } = useTranslation();

  // validation for required fields
  const requiredFields = [
    'beneficiary_name',
    'bank_address',
    'bank_name',
    'account',
    'swift',
    'state',
  ] as (keyof Partial<BankAccount>)[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '' || fields[field] === null) {
        return true;
      }
    }

    if (fields.state === BankAccountState.REJECTED && !fields.state_reason) return true;

    return false;
  };

  // elements
  const stateSelectOptions = Object.values(BankAccountState).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {t(`common.bank-account-state.${it}`)}
      </MenuItem>
    );
  });
  const typeSelectOptions = Object.values(BankAccountType).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {t(`common.bank-account-type.${it}`)}
      </MenuItem>
    );
  });

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onCancel();
  };
  const closeHandler = () => {
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('profile-bank-accounts.edit-modal.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="beneficiary_name"
            value={fields?.beneficiary_name ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-bank-accounts.edit-modal.beneficiary-name')}
            required
            fullWidth
          />
          <TextField
            name="account"
            value={fields?.account ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-bank-accounts.edit-modal.account')}
            required
            fullWidth
          />
          <TextField
            name="swift"
            value={fields?.swift ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-bank-accounts.edit-modal.swift')}
            required
            fullWidth
          />
          <TextField
            name="bank_name"
            value={fields?.bank_name ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-bank-accounts.edit-modal.bank-name')}
            required
            fullWidth
          />
          <TextField
            name="bank_address"
            value={fields?.bank_address ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-bank-accounts.edit-modal.bank-address')}
            required
            fullWidth
          />

          <TextField
            name="reference"
            value={fields?.reference ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-bank-accounts.edit-modal.reference')}
            fullWidth
            multiline
            minRows={3}
          />

          <TextField
            select
            name="state"
            value={fields?.state ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-bank-accounts.edit-modal.state')}
            fullWidth
          >
            {stateSelectOptions}
          </TextField>

          <TextField
            select
            name="type"
            value={fields?.type ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-bank-accounts.edit-modal.type')}
            fullWidth
          >
            {typeSelectOptions}
          </TextField>

          {fields?.state === BankAccountState.REJECTED && (
            <TextField
              name="state_reason"
              value={fields?.state_reason ?? ''}
              onChange={(e) => fieldsHandler(e)}
              label={t('profile-bank-accounts.edit-modal.reason')}
              fullWidth
              multiline
              minRows={3}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onSave()}
          disabled={checkSubmitDisabling()}
          //
        >
          {t('profile-bank-accounts.edit-modal.confirm-btn')}
        </Button>

        <Button variant="outlined" onClick={() => closeHandler()}>
          {t('profile-bank-accounts.edit-modal.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
